import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Params, Router, RouterModule, RouterOutlet } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucideArrowLeft, lucideChevronDown, lucideMinus, lucidePlus } from '@ng-icons/lucide';
import { ToastrSuccessComponent } from '@shared/components/toastr/toastr-success.component';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { CanEditService } from '@shared/services/can-edit.service';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogImports, HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ChipComponent } from '@shared/components/chip/chip.component';
import { EstVerrouilleService } from '@shared/services/est-verrouille.service';
import { Référentiel } from '../../administration/gestion-référentiels/models/référentiel.model';
import { Cartographie } from '../../cartographie/models/cartographie.model';
import { Parcours } from '../../référentiel-métier-parcours/models/parcours.model';
import { TYPE_ÉLÉMENT } from '../../référentiel-métier-parcours/parcours-detail/parcours-detail.component';
import {
  ParcoursStateService,
  ProfondeurActuelle,
} from '../../référentiel-métier-parcours/services/parcours-state.service';
import { ParcoursStore } from '../../référentiel-métier-parcours/store/parcours.store';
import { DialogCreationParcoursComponent } from './dialog-creation-parcours/dialog-creation-parcours.component';

@Component({
  selector: 'app-parcours-layout',
  imports: [
    RouterOutlet,
    HlmButtonImports,
    HlmIconImports,
    HlmTooltipImports,
    BrnSelectImports,
    HlmSelectImports,
    HlmDropdownMenuImports,
    HlmDialogImports,
    HlmInputImports,
    RouterModule,
    ChipComponent,
  ],
  templateUrl: './parcours-layout.component.html',
  styleUrl: './parcours-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideIcons({ lucideMinus, lucidePlus, lucideArrowLeft, lucideChevronDown })],
})
export class ParcoursLayoutComponent {
  readonly #router = inject(Router);
  readonly #parcoursStateService = inject(ParcoursStateService);
  readonly #parcoursStore = inject(ParcoursStore);
  readonly #hlmDialogService = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #canEditService = inject(CanEditService);
  readonly #estVerrouilleService = inject(EstVerrouilleService);

  readonly id = input<string | null>();
  readonly type = input<TYPE_ÉLÉMENT | null>();
  readonly roles = input.required<string[]>();

  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();
  readonly parcoursSelectionne = computed(() => this.#parcoursStore.selectedEntity());
  readonly niveauActuel = input<ProfondeurActuelle>('01', { alias: 'niveau' });
  readonly estCanvasElementsSupprimes = input<string>('false', { alias: 'canvasSupprime' });

  readonly peutModifier = computed(() =>
    this.#canEditService.check(
      this.referentiel().equipeResponsable,
      this.roles(),
      LivrablePrincipal.PARCOURS_CROISEMENT,
      this.cartographie(),
      this.referentiel(),
    ),
  );

  readonly estVerrouille = computed(() =>
    this.#estVerrouilleService.check(
      LivrablePrincipal.PARCOURS_CROISEMENT,
      this.cartographie(),
      this.referentiel(),
      this.parcoursSelectionne()?.id,
    ),
  );

  readonly parcours = computed(() => this.#parcoursStore.entities());

  protected readonly profondeurActuelle = this.#parcoursStateService.profondeurActuelle;

  quandCliqueSurUnNiveau(nouveauNiveau: ProfondeurActuelle) {
    const params: Params = { niveau: nouveauNiveau };

    const id = this.id();
    const type = this.type();

    if (id && type === 'étape' && nouveauNiveau === '02') {
      params['id'] = id;
      params['type'] = type;
    }

    this.#router.navigate([], { queryParams: params, queryParamsHandling: 'replace' });
    this.#parcoursStateService.setProfondeurActuelle(nouveauNiveau);
    this.#parcoursStore.charger({ idCartographie: this.cartographie().id, idReferentiel: this.referentiel().id });
  }

  quandCliqueSurNiveauSuivant() {
    if (this.profondeurActuelle() === '01') {
      this.quandCliqueSurUnNiveau('02');
    }
  }

  quandCliqueSurNiveauPrecedent() {
    if (this.profondeurActuelle() === '02') {
      this.quandCliqueSurUnNiveau('01');
    }
  }

  setParcoursCourant(parcours: Parcours) {
    this.#router.navigate(
      ['cartographies', this.cartographie().id, 'référentiels', this.referentiel().slug, 'parcours', parcours.id],
      {
        queryParams: { niveau: this.niveauActuel(), canvasSupprime: this.estCanvasElementsSupprimes() },
      },
    );
    this.#parcoursStore.select({ id: parcours.id });
  }

  creerParcours() {
    const dialogRef = this.#hlmDialogService.open(DialogCreationParcoursComponent, {
      context: {
        cartographie: this.cartographie().nom,
        referentiel: this.referentiel().nom,
      },
      contentClass: 'sm:!max-w-[500px]',
    });

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((nom?: string) => {
      if (!nom?.trim()) return;

      this.#parcoursStore.créer({
        idReferentiel: this.referentiel().id,
        nom: nom.trim(),
        next: (parcours) => {
          const toastrId = uuidv7();
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: `Votre parcours a été créé avec succès.`,
              id: toastrId,
            },
            id: toastrId,
          });
          this.setParcoursCourant(parcours);
        },
      });
    });
  }
}
